/**
 * footer.ts
 * Footer data cache management.
 */

import { isStale, notify } from './base';

export interface FooterData {
  info: Record<string, { description: string; copyright: string }>;
  contacts: Record<string, { address: string; phone: string; email: string }>;
  social: {
    facebook: string; instagram: string; linkedin: string; youtube: string;
    telegram: string; twitter: string; vk: string;
  };
}

const LS_FOOTER_KEY = 'sargo_footer';

const footerCache = { data: null as FooterData | null, fetchedAt: 0 };
let footerInFlight: Promise<FooterData> | null = null;

const FOOTER_INFO_DEFAULTS: FooterData['info'] = {
  ru: { description: 'Инновационные системы и решения для современных интерьеров', copyright: '© 2026 Sargo Industrial Systems. Все права защищены.' },
  uz: { description: 'Zamonaviy interyer uchun innovatsion tizimlar va yechimlar', copyright: '© 2026 Sargo Industrial Systems. Barcha huquqlar himoyalangan.' },
  en: { description: 'Innovative systems and solutions for modern interiors', copyright: '© 2026 Sargo Industrial Systems. All rights reserved.' },
};

const FOOTER_CONTACTS_DEFAULTS: FooterData['contacts'] = {
  ru: { address: 'ул. Промышленная, 1, Ташкент, Узбекистан', phone: '+998 (71) 200-00-00', email: 'info@sargo.uz' },
  uz: { address: "Sanoat ko'chasi, 1, Toshkent, O'zbekiston", phone: '+998 (71) 200-00-00', email: 'info@sargo.uz' },
  en: { address: '1 Industrial St., Tashkent, Uzbekistan', phone: '+998 (71) 200-00-00', email: 'info@sargo.uz' },
};

const FOOTER_SOCIAL_DEFAULTS: FooterData['social'] = {
  facebook: 'https://www.facebook.com/profile.php?id=61584716230422',
  instagram: 'https://www.instagram.com/sargo.uz',
  linkedin: 'https://www.linkedin.com/company/110521447',
  youtube: 'https://youtube.com/@sargo.company',
  telegram: '', twitter: '', vk: '',
};

export function getCachedFooterData(): FooterData | null {
  return footerCache.data;
}

export async function fetchFooterData(): Promise<FooterData> {
  if (footerCache.data && !isStale({ data: footerCache.data, fetchedAt: footerCache.fetchedAt })) {
    return footerCache.data;
  }
  if (footerInFlight) return footerInFlight;

  footerInFlight = (async (): Promise<FooterData> => {
    const { listCmsBlocksByPage } = await import('../adminDb');
    const result = await listCmsBlocksByPage('footer');

    const map = new Map<string, string>();
    if (result.ok) {
      for (const b of result.data) {
        if (!b.blockKey || !b.lang) continue;
        try {
          const parsed = JSON.parse(b.contentJson);
          map.set(`${b.blockKey}:${b.lang}`, typeof parsed === 'string' ? parsed : JSON.stringify(parsed));
        } catch {
          map.set(`${b.blockKey}:${b.lang}`, b.contentJson);
        }
      }
    }

    const langs = ['ru', 'uz', 'en'];
    const info: FooterData['info'] = {};
    const contacts: FooterData['contacts'] = {};

    langs.forEach(lang => {
      const description = map.get(`info.description:${lang}`);
      const copyright   = map.get(`info.copyright:${lang}`);
      info[lang] = {
        description: (description && description !== '') ? description : FOOTER_INFO_DEFAULTS[lang].description,
        copyright:   (copyright   && copyright   !== '') ? copyright   : FOOTER_INFO_DEFAULTS[lang].copyright,
      };

      const address = map.get(`contacts.address:${lang}`);
      const phone   = map.get(`contacts.phone:${lang}`);
      const email   = map.get(`contacts.email:${lang}`);
      contacts[lang] = {
        address: (address && address !== '') ? address : FOOTER_CONTACTS_DEFAULTS[lang].address,
        phone:   (phone   && phone   !== '') ? phone   : FOOTER_CONTACTS_DEFAULTS[lang].phone,
        email:   (email   && email   !== '') ? email   : FOOTER_CONTACTS_DEFAULTS[lang].email,
      };
    });

    const socialRaw = map.get('social:all');
    let social: FooterData['social'] = { ...FOOTER_SOCIAL_DEFAULTS };
    if (socialRaw) {
      try {
        const obj = JSON.parse(socialRaw);
        if (obj && typeof obj === 'object') social = { ...FOOTER_SOCIAL_DEFAULTS, ...obj };
      } catch { /* keep defaults */ }
    }

    const data: FooterData = { info, contacts, social };
    const fetchedAt = Date.now();
    footerCache.data = data;
    footerCache.fetchedAt = fetchedAt;
    footerInFlight = null;
    try { localStorage.setItem(LS_FOOTER_KEY, JSON.stringify({ data, fetchedAt })); } catch { /**/ }
    notify();
    return data;
  })().catch(() => {
    footerInFlight = null;
    if (footerCache.data) return footerCache.data;
    return { info: FOOTER_INFO_DEFAULTS, contacts: FOOTER_CONTACTS_DEFAULTS, social: FOOTER_SOCIAL_DEFAULTS };
  });

  return footerInFlight;
}

export function invalidateFooterCache() {
  footerCache.data = null;
  footerCache.fetchedAt = 0;
  footerInFlight = null;
  try { localStorage.removeItem(LS_FOOTER_KEY); } catch { /**/ }
}

// ─── Initialization ──────────────────────────────────────────────────────────

// (function preloadFooterFromStorage() {
//   try {
//     const raw = localStorage.getItem(LS_FOOTER_KEY);
//     if (!raw) return;
//     const parsed = JSON.parse(raw);
//     if (parsed && typeof parsed === 'object' && 'fetchedAt' in parsed && 'data' in parsed) {
//       footerCache.data = parsed.data;
//       footerCache.fetchedAt = parsed.fetchedAt;
//     }
//   } catch { /**/ }
// })();
